import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { type TextUIPart } from 'ai';
import { useEffect, useRef } from 'react';
import { useAiCopilot } from '../context/use-ai-copilot';
import { useChatFeedback } from '../hooks/use-chat-feedback';
import {
  chatPanelRoot,
  chatPanelScrollArea,
} from '../styles';
import { ChatHeader } from './ChatHeader';
import { ChatInput } from './ChatInput';
import { ChatMessage } from './ChatMessage';
import { EmptyState } from './EmptyState';
import { ThinkingIndicator } from './ThinkingIndicator';

function ErrorMessage({ onRetry }: { onRetry: () => void }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
      <Box
        sx={{
          px: 2,
          py: 1,
          borderRadius: '10px',
          bgcolor: '#fff5f5',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 15, color: '#ef4444' }} />
        <Typography sx={{ fontSize: 13, color: '#ef4444', fontFamily: 'Inter, sans-serif' }}>
          Something went wrong.
        </Typography>
        <IconButton
          size="small"
          onClick={onRetry}
          sx={{ p: 0.25, color: '#7e8796', '&:hover': { color: '#0acda7' } }}
        >
          <ReplayIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    </Box>
  );
}

export const ChatPanel: React.FC = () => {
  const { chat, suggestions } = useAiCopilot();
  const { messages, sendMessage, status } = chat;
  const { submitFeedback } = useChatFeedback();
  const scrollRef = useRef<HTMLDivElement>(null);

  const isSubmitting = status === 'submitted';
  const isStreaming = status === 'streaming';
  const isLoading = isSubmitting || isStreaming;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: isStreaming ? 'instant' : 'smooth' });
  }, [messages, isSubmitting, isStreaming]);

  const lastAssistantIndex = messages.reduce(
    (last, message, messageIndex) => (message.role === 'assistant' ? messageIndex : last),
    -1,
  );

  const lastAssistantMsg = messages[lastAssistantIndex];
  // Whether the last assistant message has any rendered content yet — used to
  // hide the empty bubble before the first chunk arrives.
  const lastMsgHasContent =
    lastAssistantMsg != null &&
    lastAssistantMsg.parts.some(
      (p) => p.type !== 'text' || (p as TextUIPart).text.length > 0,
    );
  // Show the indicator for the entire loading window — from submit through the
  // end of streaming — so the user keeps getting "still working" feedback even
  // while text streams in. The indicator renders below the streaming message.
  const showThinking = isLoading;

  const handleSend = (text: string) => {
    void sendMessage({ text });
  };

  const handleStop = () => {
    void chat.stop();
  };

  const isEmpty = messages.length === 0;

  return (
    <Box sx={chatPanelRoot}>
      <ChatHeader />

      {isEmpty ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <EmptyState onSuggestionClick={handleSend} suggestions={suggestions} />
        </Box>
      ) : (
        <Box ref={scrollRef} sx={chatPanelScrollArea}>
          {messages.map((message: (typeof messages)[number], index: number) => {
            if (isStreaming && index === lastAssistantIndex && !lastMsgHasContent) return null;
            return (
              <ChatMessage
                key={index}
                message={message}
                isNew={index === lastAssistantIndex}
                isLoading={isLoading && index === lastAssistantIndex}
                showSuggestions={index === lastAssistantIndex && !isLoading}
                onSuggestionClick={handleSend}
                onFeedback={submitFeedback}
              />
            );
          })}
          {showThinking && <ThinkingIndicator />}
          {status === 'error' && <ErrorMessage onRetry={() => chat.regenerate()} />}
        </Box>
      )}

      <ChatInput onSend={handleSend} onStop={handleStop} isLoading={isLoading} />
    </Box>
  );
};
